import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import * as api from "../api/client";
import type { AdminCardInfo, AdminJob, AdminPool, CatalogCard } from "../api/client";
import type { CardView } from "../api/types";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import {
  Banner,
  Field,
  NumberInput,
  Select,
  TextArea,
  TextInput,
} from "../components/admin/forms";
import { GenerateSettingsBar } from "../components/admin/GenerateSettingsBar";
import { PortraitPicker } from "../components/admin/PortraitPicker";
import { Card, naturalCardWidthPx } from "../components/Card";
import { Inspector } from "../components/Inspector";
import {
  BLANK_CARD_FORM,
  CARD_TYPES,
  FACTIONS,
  RARITIES,
  type CardFormState,
  type CardType,
  detailToForm,
  formToCardJson,
  formToPrefilled,
  formToCardView,
} from "../logic/cardForm";
import { RACES, CLASSES } from "../data/subtypes";
import { describeError } from "../logic/changesetErrors";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { adminInfoToCatalog, JobsList, Label } from "./AdminPage";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";
import { E32 } from "../theme/endesga32";
import { pixelTheme, type Theme } from "../theme/pixel";

export function CardEditorPage({ embedded = false }: { embedded?: boolean } = {}) {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();
  const params = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const returnTo =
    typeof (location.state as { returnTo?: unknown } | null)?.returnTo === "string"
      ? ((location.state as { returnTo: string }).returnTo)
      : null;

  const isNewMode = !params.id;
  const [pool, setPool] = useState<AdminPool>("main");
  const [originalId, setOriginalId] = useState<string | null>(
    isNewMode ? null : (params.id ?? null),
  );

  const [user, setUser] = useState<api.User | null>(null);
  const [form, setForm] = useState<CardFormState>(BLANK_CARD_FORM);
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pools, setPools] = useState<string[]>([]);
  const [imageVersion, setImageVersion] = useState(0);

  // Jobs UI mirrors the GENERATE tab: every AI-Fill click enqueues
  // another job and the form stays interactive — no auto-fill on
  // success. We show every recent generate_card job (not just this
  // session's) so a page refresh / server restart doesn't blank the
  // history; the server persists jobs in `admin_jobs` so the list
  // survives both. `regenJobId` is tracked separately so we can
  // bump the image version exactly once when a regen completes.
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [cards, setCards] = useState<Map<string, AdminCardInfo>>(new Map());
  const [inspect, setInspect] = useState<CatalogCard | null>(null);
  const [regenJobId, setRegenJobId] = useState<string | null>(null);
  const [portraitPickerOpen, setPortraitPickerOpen] = useState(false);
  const [usingPortrait, setUsingPortrait] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const me = await api.getMe();
        setUser(me.user);
      } catch {
        // header degrades to no-user
      }
    })();
  }, []);

  // Pools list — drives the set selector and faction-partitioning logic.
  useEffect(() => {
    void (async () => {
      try {
        const { pools } = await api.listAdminCards({ page: 1, per_page: 1 });
        setPools(pools);
      } catch {
        // best-effort; selector falls back to a hardcoded list below
      }
    })();
  }, []);

  // Load existing card.
  useEffect(() => {
    if (isNewMode) return;
    if (!params.id) return;
    setLoading(true);
    void (async () => {
      try {
        const detail = await api.getAdminCard(params.id as string,
        );
        setForm(detailToForm(detail));
        setPool(detail.pool);
        setOriginalId(detail.id);
        setError(null);
      } catch (err) {
        setError(describeError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [isNewMode, params.id]);

  // Honor a `?prefill=base64-json` query param on /admin/card-editor/new
  // so the SPA can deep-link with a partial card. Currently unused by
  // the rest of the SPA but cheap to keep — handy for "duplicate this
  // card" flows down the road.
  useEffect(() => {
    if (!isNewMode) return;
    const raw = searchParams.get("prefill");
    if (!raw) return;
    try {
      const decoded = JSON.parse(atob(raw));
      if (decoded && typeof decoded === "object") {
        setForm((prev) => ({ ...prev, ...(decoded as Partial<CardFormState>) }));
      }
    } catch {
      // ignore malformed prefill
    }
  }, [isNewMode, searchParams]);

  const updateField = useCallback(<K extends keyof CardFormState>(key: K, value: CardFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ---------- snapshot for the live preview ----------
  const previewCardView = useMemo<CardView>(
    () => formToCardView(form, originalId, pool, imageVersion),
    [form, originalId, pool, imageVersion],
  );

  // ---------- job + card-roster polling ----------
  // Same shape as the GENERATE tab: every 2s we re-fetch the jobs
  // list and the admin cards roster so the JobRow can render saved
  // ids as clickable inspector links the moment they exist on disk.
  // The polling is unconditional (cheap) so it picks up jobs even
  // after a refresh of this page mid-run.
  const loadJobsAndCards = useCallback(async () => {
    try {
      const [{ jobs: allJobs }, { cards: allCards }] = await Promise.all([
        api.listAdminJobs(),
        api.listAdminCards(),
      ]);
      setJobs(allJobs);
      setCards(new Map(allCards.map((c) => [c.id, c])));
    } catch {
      // transient; keep polling
    }
  }, []);

  useEffect(() => {
    void loadJobsAndCards();
    const id = setInterval(loadJobsAndCards, 2000);
    return () => clearInterval(id);
  }, [loadJobsAndCards]);

  // Watch for the regen-image job to finish so the live preview can
  // bust its image cache. AI-Fill jobs intentionally don't touch the
  // form — they show up in the jobs list, and the user clicks a saved
  // id if they want to inspect the result.
  useEffect(() => {
    if (!regenJobId) return;
    const job = jobs.find((j) => j.id === regenJobId);
    if (!job) return;
    if (job.status === "succeeded") {
      setImageVersion((v) => v + 1);
      setInfo("Image regenerated.");
      setRegenJobId(null);
    } else if (job.status === "failed") {
      setError(`Regen image failed: ${job.error ?? "unknown"}`);
      setRegenJobId(null);
    }
  }, [jobs, regenJobId]);

  // The list rendered below the form: every recent text-gen + image-gen
  // job in the system (the editor and the GENERATE tab share the
  // same persisted history). Sorted newest-first by the API.
  const editorGenerateJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.kind === "generate_card_text" || j.kind === "generate_card_image",
      ),
    [jobs],
  );

  const onInspectSavedId = useCallback(
    (id: string) => {
      const info = cards.get(id);
      if (!info) {
        setError(
          `Card "${id}" isn't in the admin roster yet — wait a moment and try again`,
        );
        return;
      }
      setInspect(adminInfoToCatalog(info));
    },
    [cards],
  );

  // ---------- actions ----------
  async function onSave() {
    if (!form.id.trim()) {
      setError("ID is required.");
      return;
    }
    if (form.type === "" || form.faction === "" || form.rarity === "") {
      // ANY is meaningful for AI Fill (LLM picks at random) but a
      // saved card needs a concrete value on disk. Catch it here so
      // the user gets a clean message instead of a server 422.
      setError(
        "Pick a Type, Faction, and Rarity before saving — ANY is only for AI Fill.",
      );
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const json = formToCardJson(form);
      const lua = form.script.trim() ? form.script : null;

      // Always send `faction`. The server is the source of truth on
      // which pools are faction-partitioned (`base_set` / `npc_cards`
      // today): partitioned destinations use the value, flat ones
      // (`high_fantasia`) ignore it. Lifting that logic out of the
      // client means the SPA doesn't have to track which pools need
      // faction segments — the form always has a faction value.
      if (isNewMode) {
        await api.createAdminCard({
          pool: form.pool,
          id: form.id,
          json: JSON.stringify(json, null, 2),
          lua,
          faction: form.faction,
        });
        setOriginalId(form.id);
        setPool(form.pool);
        setInfo("Card created.");
        navigate(`/admin/card-editor/${form.id}/edit`, {
          replace: true,
        });
      } else {
        // If the pool changed, move the card on disk first, then save
        // content at the new location. `expansion_set` is metadata; it
        // doesn't route disk location any more.
        if (form.pool !== pool) {
          await api.moveAdminCard(originalId ?? form.id, {
            new_pool: form.pool,
            faction: form.faction,
          });
          setPool(form.pool);
          navigate(
            `/admin/card-editor/${originalId ?? form.id}/edit`,
            { replace: true },
          );
        }
        await api.saveAdminCard(originalId ?? form.id, {
          json: JSON.stringify(json, null, 2),
          lua,
        });
        setInfo("Saved.");
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  }

  async function onAIFill() {
    setError(null);
    setInfo(null);
    try {
      const prefilled = formToPrefilled(form);
      await api.enqueueFillCard(prefilled);
      // Force-refresh so the new job appears immediately rather than
      // on the next 2s tick. The form stays interactive — the user
      // can tweak fields and submit another job immediately.
      void loadJobsAndCards();
    } catch (err) {
      setError(describeError(err));
    }
  }

  async function onDeleteCard(card: CatalogCard) {
    if (!card.expansion_set) {
      setError("Card has no pool — can't delete.");
      return;
    }
    const proceed = window.confirm(
      `Delete "${card.name ?? card.id}"?\n\n` +
        "This removes the card directory from disk AND wipes every " +
        "DB row that references it (claims, collections, deck_cards). " +
        "Irreversible.",
    );
    if (!proceed) return;
    setError(null);
    setInfo(null);
    try {
      const r = await api.deleteAdminCard(card.id,
      );
      setInspect(null);
      void loadJobsAndCards();
      const { claims, collections, deck_cards, shop_rotations } = r.counts;
      setInfo(
        `Deleted ${card.id}. Cleaned up ${claims} claim(s), ${collections} ` +
          `collection row(s), ${deck_cards} deck row(s), ${shop_rotations} ` +
          `shop rotation row(s).`,
      );
    } catch (err) {
      setError(describeError(err));
    }
  }

  async function onUseNpcImage(filename: string) {
    if (!originalId) {
      setError("Save the card first before swapping its image.");
      return;
    }
    setError(null);
    setInfo(null);
    setUsingPortrait(true);
    try {
      await api.useAdminPortrait(originalId, filename);
      setImageVersion((v) => v + 1);
      setInfo(`Image replaced with ${filename}.`);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setUsingPortrait(false);
    }
  }

  async function onRegenImage() {
    if (!originalId) {
      setError("Save the card first before regenerating its image.");
      return;
    }
    setError(null);
    setInfo(null);
    try {
      const { job_id } = await api.enqueueRegenImage(originalId);
      setRegenJobId(job_id);
    } catch (err) {
      setError(describeError(err));
    }
  }

  function onClearForm() {
    // New-card mode: just blank the form fields. We deliberately
    // don't navigate or change the URL — the user is staying on the
    // /new page. In edit mode this resets local edits back to a
    // blank slate (the user can re-load from the URL by refreshing).
    setForm(BLANK_CARD_FORM);
    setError(null);
    setInfo(null);
  }

  function onCancel() {
    navigate("/admin/cards");
  }

  const breadcrumb = isNewMode
    ? "ADMIN · CARD EDITOR · NEW"
    : `ADMIN · CARD EDITOR · ${form.name || form.id}`;

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        background: embedded ? "transparent" : theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: embedded ? 0 : "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {!embedded && (
        <>
          <AppHeader
            user={user}
            backTo={returnTo ?? "/admin/cards"}
            backLabel={returnTo ? "Back to Deck" : "Back to Cards"}
          />
          <Breadcrumb text={breadcrumb} />
        </>
      )}

      {error && <Banner kind="error" theme={theme}>{error}</Banner>}
      {info && <Banner kind="info" theme={theme}>{info}</Banner>}
      {regenJobId && (
        <Banner kind="info" theme={theme}>
          Regenerating image…
        </Banner>
      )}

      {loading ? (
        <div style={{ color: theme.inkFaint }}>Loading…</div>
      ) : (
        // Vertical layout: form + preview on top (single horizontal
        // row), jobs list below. The whole page scrolls — the form
        // and jobs list each get as much vertical room as they need.
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            paddingRight: 4,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: narrow
                ? "minmax(0, 1fr)"
                : "minmax(0, 1fr) 280px",
              gap: 20,
            }}
          >
            <FormPanel
              theme={theme}
              form={form}
              pool={pool}
              isNewMode={isNewMode}
              pools={pools}
              saving={saving}
              regenActive={regenJobId !== null}
              usingPortrait={usingPortrait}
              update={updateField}
              onSave={onSave}
              onAIFill={onAIFill}
              onRegenImage={onRegenImage}
              onUseNpcImage={() => setPortraitPickerOpen(true)}
              onClearForm={onClearForm}
              onCancel={onCancel}
              onSettingsError={setError}
            />
            <PreviewPanel theme={theme} snapshot={previewCardView} />
          </div>

          <section style={{ display: "grid", gap: 8 }}>
            <Label theme={theme}>AI FILL JOBS</Label>
            <div style={{ fontSize: 12, color: theme.inkDim }}>
              Each AI Fill click enqueues a separate job and writes the
              generated card to disk automatically. The form stays
              editable while jobs run — tweak fields and submit again.
              Click a saved card id below to inspect it.
            </div>
            <JobsList
              jobs={editorGenerateJobs}
              theme={theme}
              onInspect={onInspectSavedId}
              readyIds={new Set(cards.keys())}
            />
          </section>
        </div>
      )}

      <PortraitPicker
        open={portraitPickerOpen}
        theme={theme}
        onClose={() => setPortraitPickerOpen(false)}
        onPick={(filename) => void onUseNpcImage(filename)}
      />

      <Inspector
        card={inspect}
        theme={theme}
        onClose={() => setInspect(null)}
        secondaryActions={
          inspect ? (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  if (!inspect.expansion_set) return;
                  navigate(
                    `/admin/card-editor/${inspect.id}/edit`,
                  );
                  setInspect(null);
                }}
                disabled={!inspect.expansion_set}
                title={
                  inspect.expansion_set
                    ? undefined
                    : "This card has no pool — refresh and try again."
                }
                {...withBtnStyle(defaultBtn(theme, !inspect.expansion_set), {
                  fontSize: 11,
                  padding: "8px 16px",
                })}
              >
                EDIT
              </button>
              <button
                onClick={() => void onDeleteCard(inspect)}
                {...withBtnStyle(defaultBtn(theme, false), {
                  fontSize: 11,
                  padding: "8px 16px",
                  background: E32.scarlet,
                  color: E32.parchment,
                  border: `2px solid ${E32.scarlet}`,
                })}
              >
                DELETE
              </button>
            </div>
          ) : null
        }
      />
    </div>
  );
}

// ---------- form panel ----------

function FormPanel({
  theme,
  form,
  pool,
  isNewMode,
  pools,
  saving,
  regenActive,
  usingPortrait,
  update,
  onSave,
  onAIFill,
  onRegenImage,
  onUseNpcImage,
  onClearForm,
  onCancel,
  onSettingsError,
}: {
  theme: Theme;
  form: CardFormState;
  pool: AdminPool;
  isNewMode: boolean;
  pools: string[];
  saving: boolean;
  regenActive: boolean;
  usingPortrait: boolean;
  update: <K extends keyof CardFormState>(k: K, v: CardFormState[K]) => void;
  onSave: () => void;
  onAIFill: () => void;
  onRegenImage: () => void;
  onUseNpcImage: () => void;
  onClearForm: () => void;
  onCancel: () => void;
  onSettingsError: (msg: string | null) => void;
}) {
  // The dropdown lists POOLS, not expansion sets. (Pre-rename these
  // were the same field; now `pool` is the on-disk location and
  // `expansion_set` is content-pack metadata.) Fall back to a hardcoded
  // list if the admin-cards index hasn't responded yet.
  const poolOptions = pools.length > 0 ? pools : ["base_set", "main", "npc_cards", "gen"];
  const isCreature = form.type === "creature";

  return (
    <div
      style={{
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <GenerateSettingsBar
        onError={onSettingsError}
        description="Server-wide. Drives both the GENERATE tab and the AI Fill button below — toggle VALIDATE here to run the rules-validator on AI-Filled cards."
      />

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {/* SAVE only appears in edit mode — for a new card, AI Fill
         *  is the only path forward and it auto-saves to disk. */}
        {!isNewMode && (
          <button
            onClick={onSave}
            disabled={saving}
            {...withBtnStyle(primaryBtn(theme, saving), {
              fontSize: 11,
              padding: "10px 16px",
            })}
          >
            {saving ? "SAVING…" : "SAVE"}
          </button>
        )}
        <button
          onClick={onAIFill}
          disabled={saving}
          title="AI Fill writes the generated card to disk automatically — no Save click needed."
          {...withBtnStyle(defaultBtn(theme, saving), {
            fontSize: 11,
            padding: "10px 16px",
          })}
        >
          AI FILL
        </button>
        <button
          onClick={onClearForm}
          disabled={saving}
          {...withBtnStyle(defaultBtn(theme, saving), {
            fontSize: 11,
            padding: "10px 16px",
          })}
        >
          CLEAR FORM
        </button>
        <button
          onClick={onRegenImage}
          disabled={saving || regenActive || isNewMode}
          title={isNewMode ? "Save the card first." : undefined}
          {...withBtnStyle(defaultBtn(theme, saving || regenActive || isNewMode), {
            fontSize: 11,
            padding: "10px 16px",
          })}
        >
          {regenActive ? "REGENERATING…" : "REGEN IMAGE"}
        </button>
        <button
          onClick={onUseNpcImage}
          disabled={saving || regenActive || usingPortrait || isNewMode}
          title={isNewMode ? "Save the card first." : undefined}
          {...withBtnStyle(
            defaultBtn(
              theme,
              saving || regenActive || usingPortrait || isNewMode,
            ),
            { fontSize: 11, padding: "10px 16px" },
          )}
        >
          {usingPortrait ? "COPYING…" : "USE NPC IMAGE"}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          {...withBtnStyle(defaultBtn(theme, saving), {
            fontSize: 11,
            padding: "10px 16px",
            marginLeft: "auto",
          })}
        >
          CLOSE
        </button>
      </div>
      <div
        style={{
          fontSize: 11,
          color: theme.inkFaint,
          fontStyle: "italic",
        }}
      >
        AI Fill auto-saves the generated card to disk — no Save click
        needed. Submit multiple times to queue multiple jobs; jobs run
        one at a time and appear below.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field theme={theme} label="ID" hint={isNewMode ? "snake_case, unique" : "(immutable)"}>
          <TextInput
            theme={theme}
            value={form.id}
            disabled={!isNewMode}
            onChange={(v) => update("id", v)}
            placeholder="e.g. blasting_imp"
          />
        </Field>
        <Field theme={theme} label="Name">
          <TextInput
            theme={theme}
            value={form.name}
            onChange={(v) => update("name", v)}
            placeholder="Display name"
          />
        </Field>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 12,
        }}
      >
        <Field theme={theme} label="Type">
          <Select
            theme={theme}
            value={form.type}
            onChange={(v) => update("type", v as CardType | "")}
            options={[
              { value: "", label: "(any — random)" },
              ...CARD_TYPES.map((t) => ({ value: t, label: t })),
            ]}
          />
        </Field>
        <Field theme={theme} label="Faction">
          <Select
            theme={theme}
            value={form.faction}
            onChange={(v) => update("faction", v)}
            options={[
              { value: "", label: "(any — random)" },
              ...FACTIONS.map((f) => ({ value: f, label: f })),
            ]}
          />
        </Field>
        <Field theme={theme} label="Rarity">
          <Select
            theme={theme}
            value={form.rarity}
            onChange={(v) => update("rarity", v)}
            options={[
              { value: "", label: "(any — random)" },
              ...RARITIES.map((r) => ({ value: r, label: r })),
            ]}
          />
        </Field>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 12,
        }}
      >
        <Field theme={theme} label="Cost">
          <NumberInput
            theme={theme}
            value={form.cost}
            onChange={(v) => update("cost", v)}
          />
        </Field>
        <Field theme={theme} label="Attack" hint={isCreature ? "" : "(0 for non-creature)"}>
          <NumberInput
            theme={theme}
            value={form.attack}
            onChange={(v) => update("attack", v)}
          />
        </Field>
        <Field theme={theme} label="Life" hint={isCreature ? "" : "(0 for non-creature)"}>
          <NumberInput
            theme={theme}
            value={form.life}
            onChange={(v) => update("life", v)}
          />
        </Field>
      </div>

      {/* Subtypes are creature-only — hide the row entirely for spells,
       *  enchantments, and traps (formToCardJson also drops them there). */}
      {isCreature && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 12,
          }}
        >
          <Field theme={theme} label="Race" hint="creature subtype">
            <Select
              theme={theme}
              value={form.race}
              onChange={(v) => update("race", v)}
              options={[
                { value: "", label: "(none)" },
                ...RACES.map((r) => ({ value: r, label: r })),
              ]}
            />
          </Field>
          <Field theme={theme} label="Class" hint="creature subtype">
            <Select
              theme={theme}
              value={form.class}
              onChange={(v) => update("class", v)}
              options={[
                { value: "", label: "(none)" },
                ...CLASSES.map((c) => ({ value: c, label: c })),
              ]}
            />
          </Field>
        </div>
      )}

      <Field
        theme={theme}
        label="Pool"
        hint={
          form.pool !== pool && !isNewMode
            ? `Move from ${pool} → ${form.pool} on save`
            : ""
        }
      >
        <Select
          theme={theme}
          value={form.pool}
          onChange={(v) => update("pool", v)}
          options={poolOptions.map((s) => ({ value: s, label: s }))}
        />
      </Field>

      <Field theme={theme} label="Expansion set">
        <Select
          theme={theme}
          value={form.expansion_set}
          onChange={(v) => update("expansion_set", v)}
          options={[{ value: "high_fantasia", label: "high_fantasia" }]}
        />
      </Field>

      <Field theme={theme} label="Rules text">
        <TextArea
          theme={theme}
          value={form.rules_text}
          onChange={(v) => update("rules_text", v)}
          rows={3}
          placeholder="Sneaky.&#10;When this creature deals damage to the opponent, the opponent discards a random card."
        />
      </Field>

      <Field theme={theme} label="Flavor">
        <TextArea
          theme={theme}
          value={form.flavor}
          onChange={(v) => update("flavor", v)}
          rows={2}
        />
      </Field>

      <Field theme={theme} label="Image generation prompt">
        <TextArea
          theme={theme}
          value={form.image_generation_prompt}
          onChange={(v) => update("image_generation_prompt", v)}
          rows={3}
        />
      </Field>

      <Field theme={theme} label="card.lua" hint="leave blank for vanilla / no script">
        <TextArea
          theme={theme}
          value={form.script}
          onChange={(v) => update("script", v)}
          rows={12}
          monospace
          placeholder={"function on_play(ctx)\n    grant_keyword(ctx.self, \"sneaky\")\nend"}
        />
      </Field>

      <div
        style={{
          padding: 10,
          border: `1px dashed ${theme.panelEdge}`,
          background: theme.panelSolid,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.inkFaint,
          }}
        >
          AI FILL INPUTS
        </div>
        <div style={{ fontSize: 11, color: theme.inkDim }}>
          Hints the next AI Fill will use. Leave blank to let the
          sampler pick at random.
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 120px",
            gap: 12,
            alignItems: "end",
          }}
        >
          <Field
            theme={theme}
            label="Seed words"
            hint="comma- or space-separated"
          >
            <TextInput
              theme={theme}
              value={form.seed_words}
              onChange={(v) => update("seed_words", v)}
              placeholder="e.g. lantern, fuse, cellar"
            />
          </Field>
          <Field theme={theme} label="Typicality" hint="1-10">
            <NumberInput
              theme={theme}
              value={form.typicality}
              onChange={(v) => update("typicality", v)}
            />
          </Field>
        </div>
        <Field
          theme={theme}
          label="Notes"
          hint="free-form guidance routed into the prompt"
        >
          <TextArea
            theme={theme}
            value={form.notes}
            onChange={(v) => update("notes", v)}
            rows={3}
            placeholder={
              "e.g. lean gross, not heroic; should reference Tordrin's quest"
            }
          />
        </Field>
      </div>
    </div>
  );
}

function PreviewPanel({ theme, snapshot }: { theme: Theme; snapshot: CardView }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        alignItems: "center",
        padding: 16,
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        PREVIEW
      </div>
      <div style={{ width: naturalCardWidthPx("normal") }}>
        <Card card={snapshot} size="normal" theme={theme} />
      </div>
    </div>
  );
}

